import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { useCreateDaily } from '../hooks/use-menu-mutations'
import type { DailyExistsResult, MenuCreateMode } from '@/api/types'
import { ApiError } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface CreateDailyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  restaurantId: number
  targetDate: string // YYYY-MM-DD
}

export function CreateDailyDialog({
  open,
  onOpenChange,
  restaurantId,
  targetDate,
}: CreateDailyDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const navigate = useNavigate()
  const createDaily = useCreateDaily()
  const [mode, setMode] = useState<MenuCreateMode>('fromMaster')
  const [fromDate, setFromDate] = useState('')

  const openEditor = (menuId: number) => {
    onOpenChange(false)
    navigate({ to: '/menus/$menuId', params: { menuId: String(menuId) } })
  }

  const handleCreate = async () => {
    try {
      const res = await createDaily.mutateAsync({
        restaurantId,
        type: 'daily',
        menuDate: targetDate,
        mode,
        fromDate: mode === 'fromDate' ? fromDate : undefined,
      })
      openEditor(res.id)
    } catch (error) {
      // A daily already exists for this date: open it instead.
      if (error instanceof ApiError && error.code === 'daily_exists') {
        const existing = (error.data as DailyExistsResult).existingMenuId
        openEditor(existing)
      }
    }
  }

  const canSubmit =
    !createDaily.isPending && (mode !== 'fromDate' || fromDate !== '')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {tMenus('create.title', { date: targetDate })}
          </DialogTitle>
          <DialogDescription>{tMenus('create.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>{tMenus('create.modeLabel')}</Label>
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as MenuCreateMode)}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fromMaster">
                  {tMenus('create.fromMaster')}
                </SelectItem>
                <SelectItem value="fromDate">
                  {tMenus('create.fromDate')}
                </SelectItem>
                <SelectItem value="empty">{tMenus('create.empty')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'fromDate' && (
            <div>
              <Label htmlFor="from-date">{tMenus('create.sourceDate')}</Label>
              <Input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1.5"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tMenus('create.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={!canSubmit}>
            {tMenus('create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
